import { z } from 'zod';

// ---------------------------------------------------------------------------
// Chatter simulator schemas.
//
// Two contracts live here, deliberately split:
//
//   * ChatterConversationPackSchema - the file a user uploads. Pure content:
//     personas and conversations (who says what, replies, reactions, the kind
//     of conversation). It names no game, no channel ids, and no timestamps,
//     so the same pack is reusable across games and across timelines.
//
//   * ChatterIngestRequestSchema - the wire payload the dev web tool POSTs to
//     the bot's dev-only ingestion endpoint. It pins the pack to a target game,
//     routes conversation kinds to real channels, and supplies the timeline /
//     density spec. This is the external input the bot validates at its
//     boundary before streaming the pack through the real Discord persist path.
//
// Both are dev tooling: nothing here is reachable in production (the endpoint
// only mounts when NODE_ENV === 'development').
// ---------------------------------------------------------------------------

// A Discord snowflake stored as text (ids overflow 32-bit ints).
const SnowflakeSchema = z.string().regex(/^\d{17,20}$/, 'must be a Discord snowflake');

// ---- Conversation pack (the uploaded file) --------------------------------

// A fake Discord author. `authorId` is optional: the runner derives a stable
// snowflake from `ref` when it is omitted, so a persona stays consistent across
// runs without the author having to invent ids.
export const ChatterPersonaSchema = z.object({
  ref: z.string().min(1),
  authorTag: z.string().min(1),
  authorId: SnowflakeSchema.optional(),
  bot: z.boolean().default(false),
});
export type ChatterPersona = z.infer<typeof ChatterPersonaSchema>;

// Unicode emoji carry id=null and name='👍'; custom guild emoji carry an id and
// a name code. We keep the union so reactions can model either.
export const ChatterEmojiSchema = z.object({
  name: z.string().min(1),
  id: SnowflakeSchema.nullable().default(null),
  animated: z.boolean().nullable().default(null),
});
export type ChatterEmoji = z.infer<typeof ChatterEmojiSchema>;

export const ChatterMessageSchema = z.object({
  ref: z.string().min(1),
  persona: z.string().min(1),
  content: z.string(),
  // Another message `ref` in the same conversation. Drives a Discord reply
  // (message type 19) when set.
  replyTo: z.string().min(1).nullable().default(null),
});
export type ChatterMessage = z.infer<typeof ChatterMessageSchema>;

export const ChatterReactionSchema = z.object({
  // The message `ref` this reaction lands on.
  on: z.string().min(1),
  // The reacting persona `ref`.
  persona: z.string().min(1),
  emoji: ChatterEmojiSchema,
});
export type ChatterReaction = z.infer<typeof ChatterReactionSchema>;

export const ChatterConversationSchema = z.object({
  ref: z.string().min(1),
  // Free-form label (bug, suggestion, vibes, question...). The UI routes a
  // conversation to a channel by matching this against its routing rules.
  kind: z.string().min(1),
  // Forum-style: every message in the conversation shares a thread_id so the
  // portal groups them as one post + replies.
  thread: z.boolean().default(false),
  messages: z.array(ChatterMessageSchema).min(1),
  reactions: z.array(ChatterReactionSchema).default([]),
});
export type ChatterConversation = z.infer<typeof ChatterConversationSchema>;

export const ChatterConversationPackSchema = z.object({
  title: z.string().default(''),
  personas: z.array(ChatterPersonaSchema).min(1),
  conversations: z.array(ChatterConversationSchema).min(1),
});
export type ChatterConversationPack = z.infer<typeof ChatterConversationPackSchema>;

// ---- Ingest request (the wire payload) ------------------------------------

// The target game, addressed by id or slug. The bot resolves the studio +
// Discord connection from it.
export const ChatterTargetSchema = z
  .object({
    gameId: z.string().uuid().optional(),
    gameSlug: z.string().min(1).optional(),
  })
  .refine((t) => t.gameId !== undefined || t.gameSlug !== undefined, {
    message: 'target requires gameId or gameSlug',
  });
export type ChatterTarget = z.infer<typeof ChatterTargetSchema>;

// A concrete channel a conversation can be routed into. `parent*` is only
// meaningful for forum threads (the forum name); the runner mirrors the
// normaliser, which drops the parent for top-level channels.
export const ChatterChannelSchema = z.object({
  channelId: SnowflakeSchema,
  channelName: z.string().min(1),
  parentChannelId: SnowflakeSchema.nullable().default(null),
  parentChannelName: z.string().nullable().default(null),
});
export type ChatterChannel = z.infer<typeof ChatterChannelSchema>;

export const ChatterRoutingRuleSchema = ChatterChannelSchema.extend({
  kind: z.string().min(1),
});
export type ChatterRoutingRule = z.infer<typeof ChatterRoutingRuleSchema>;

// Maps conversation kinds to channels. A conversation whose kind has no rule
// falls back to `fallback`; a pack with an unroutable conversation and no
// fallback is rejected at layout time.
export const ChatterRoutingSchema = z
  .object({
    rules: z.array(ChatterRoutingRuleSchema).default([]),
    fallback: ChatterChannelSchema.optional(),
  })
  .refine((r) => r.rules.length > 0 || r.fallback !== undefined, {
    message: 'routing needs at least one rule or a fallback channel',
  });
export type ChatterRouting = z.infer<typeof ChatterRoutingSchema>;

// A burst window: conversations are `weight` times more likely to land inside
// it than in the quiet baseline (a playtest weekend, a patch-day spike).
export const ChatterBurstSchema = z.object({
  from: z.string().datetime({ offset: true }),
  to: z.string().datetime({ offset: true }),
  weight: z.number().positive().default(3),
});
export type ChatterBurst = z.infer<typeof ChatterBurstSchema>;

// The density spec. Conversations spread across [from, to]; short spans read
// busy, long spans read quiet, and bursts cluster activity into windows.
export const ChatterTimelineSchema = z
  .object({
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    bursts: z.array(ChatterBurstSchema).default([]),
    // Deterministic layout: the same seed reproduces the same scatter.
    seed: z.number().int().default(1),
  })
  .refine((t) => new Date(t.from).getTime() < new Date(t.to).getTime(), {
    message: 'timeline `from` must be before `to`',
  });
export type ChatterTimeline = z.infer<typeof ChatterTimelineSchema>;

export const ChatterIngestRequestSchema = z.object({
  target: ChatterTargetSchema,
  routing: ChatterRoutingSchema,
  timeline: ChatterTimelineSchema,
  pack: ChatterConversationPackSchema,
  // When true, the bot lays out the timeline and returns density counts without
  // writing anything - powers the web tool's preview sparkline.
  dryRun: z.boolean().default(false),
});
export type ChatterIngestRequest = z.infer<typeof ChatterIngestRequestSchema>;
